<template>
  <router-link
    v-if="link.to"
    v-tooltip="tooltipConfig"
    custom
    :to="link.to"
    v-slot="{ href, navigate, isActive }"
  >
    <a
      class="side-bar__menu-item"
      :class="getMenuItemClasses(isActive)"
      :href="href"
      @click="navigate"
    >
      <div class="side-bar__menu-item-content">
        <div class="side-bar__menu-item-icon-wrap">
          <div
            v-show="isShrunk && hasCounter"
            class="side-bar__counter side-bar__counter--short"
          >
            {{ shortCounterValue }}
          </div>

          <gl-icon
            class="side-bar__menu-item-icon"
            :height="link.iconSize || 20"
            :hover="link.iconHover"
            :name="isActive ? link.activeIcon : link.icon"
            :tone="isActive ? link.activeIconTone : link.iconTone"
            :width="link.iconSize || 20"
          />
        </div>

        <span
          v-show="!isShrunk"
          class="side-bar__menu-item-title"
        >
          {{ link.title }}
        </span>

        <div
          v-show="!isShrunk && hasCounter"
          class="side-bar__counter side-bar__counter--full"
        >
          {{ counterValue }}
        </div>

        <div
          v-if="link.notifyKey && hasNotify(link.notifyKey)"
          class="side-bar__warning-noty"
        />
      </div>
    </a>
  </router-link>

  <button
    v-else
    v-tooltip="tooltipConfig"
    class="side-bar__menu-item side-bar__menu-item--button"
    :class="getMenuItemClasses(false)"
    type="button"
    @click="handleClick"
  >
    <div class="side-bar__menu-item-content">
      <div class="side-bar__menu-item-icon-wrap">
        <gl-icon
          class="side-bar__menu-item-icon"
          :height="link.iconSize || 20"
          :hover="link.iconHover"
          :name="link.icon"
          :tone="link.iconTone"
          :width="link.iconSize || 20"
        />
      </div>

      <span
        v-show="!isShrunk"
        class="side-bar__menu-item-title"
      >
        {{ link.title }}
      </span>
    </div>
  </button>
</template>

<script>
import GlIcon from '@/components/gl-icon'

export default {
  name: 'SidebarMenuItem',

  components: {
    GlIcon,
  },

  props: {
    link: {
      type: Object,
      required: true,
    },
    isShrunk: {
      type: Boolean,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
    isTablet: {
      type: Boolean,
      required: true,
    },
    hasNotify: {
      type: Function,
      required: true,
    },
  },

  computed: {
    tooltipConfig() {
      return {
        content: this.link.tooltip,
        visible: this.isShrunk && !this.isMobile && !this.isTablet,
        class: 'tooltip--sidebar',
      }
    },

    hasCounter() {
      return Number(this.link.counter) > 0
    },

    counterValue() {
      const limit = this.link.counterLimit || 1000
      return this.link.counter > limit ? `${limit}+` : this.link.counter
    },

    shortCounterValue() {
      const limit = this.link.shortCounterLimit || 99
      return this.link.counter > limit ? `${limit}+` : this.link.counter
    },
  },

  methods: {
    getMenuItemClasses(isActive) {
      return {
        'side-bar__menu-item--active': isActive,
        'side-bar__menu-item--shrunk': this.isShrunk,
      }
    },

    handleClick() {
      if (typeof this.link.action === 'function') {
        this.link.action()
      }
    },
  },
}
</script>

<style scoped>
.side-bar__menu-item {
  display: flex;
  width: 100%;
  height: 36px;
  align-items: center;
  flex-shrink: 0;
  color: var(--Basic-900, #091540) !important;
  font-size: var(--Size-S, 12px);
  font-weight: 600;
  line-height: var(--Line-Height-S, 16px);
  letter-spacing: var(--Letter-Spacing-None, 0);
  text-decoration: none;
  cursor: pointer;
  margin-bottom: 1px;
}

.side-bar__menu-item--button {
  border: none;
  background: transparent;
  font-family: inherit;
}

.side-bar__menu-item--shrunk {
  width: 36px;
  max-width: 36px;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
}

.side-bar__menu-item-content {
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 40px;
  padding: 0 6px 0 8px;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.side-bar__menu-item:hover .side-bar__menu-item-content {
  background-color: var(--Basic-100);
}

.side-bar__menu-item--active .side-bar__menu-item-content {
  background-color: var(--Brand-100);
}

.side-bar__menu-item--active:hover .side-bar__menu-item-content {
  background-color: var(--Brand-200);
}

.side-bar__menu-item--shrunk .side-bar__menu-item-content {
  justify-content: center;
  max-width: 36px;
  min-width: 36px;
  max-height: 36px;
  padding: 0;
}

.side-bar__menu-item-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.side-bar__menu-item-icon {
  display: block;
  min-width: 20px;
}

.side-bar__menu-item-title {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.side-bar__counter {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--Error-Main, #FF4754);
  color: var(--white, #fff);
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 13px;
  letter-spacing: var(--Letter-Spacing-None, 0);
  text-align: center;
  text-transform: uppercase;
  border: 2px solid var(--white, #fff);
}

.side-bar__counter--full {
  height: 22px;
  min-width: 22px;
  padding: 0 4px;
  margin-left: auto;
  border-radius: 11px;
  transform: scale(1);
  transition:
    transform 0.18s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.18s ease;
  will-change: transform;
}

.side-bar__counter--full:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.side-bar__counter--short {
  position: absolute;
  right: -6px;
  top: -6px;
  height: 18px;
  padding: 0 4px;
  border-radius: 16px;
}

.side-bar__warning-noty {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--Error-Main, #FF4754);
  transform-origin: center;
  animation: pulse-noty 2.5s infinite cubic-bezier(0.25, 1, 0.5, 1);
}

@keyframes pulse-noty {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 4px rgba(255, 71, 84, 0.6);
  }

  50% {
    opacity: 0.15;
    transform: scale(0.6);
    box-shadow: 0 0 0 rgba(255, 71, 84, 0);
  }
}
</style>
